/**
 * Cálculo de importes fiscales con aritmética entera en céntimos (FISC-10).
 *
 * Por qué céntimos enteros: los importes de factura se calculan hoy con
 * números en coma flotante y se redondean con `Math.round(n*100)/100` o
 * `toFixed(2)`. 2.425,50 × 0,21 = 509,355 se almacena como
 * 509,35499999999997 y redondea hacia abajo a 509,35 (debería ser 509,36);
 * 3.950,50 + 829,605 se almacena como 4.780,10499… y redondea a 4.780,10
 * (debería ser 4.780,11). FACe rechaza facturas cuyos importes no cuadran
 * aunque sea por un céntimo. Trabajar en céntimos enteros, con un redondeo
 * robusto al ruido de la representación en coma flotante, elimina esta
 * clase de error de raíz.
 *
 * Por qué agregación por tipo de IVA: el bloque `TaxesOutputs` del Facturae
 * y el desglose de VeriFactu son autoritativos a nivel de factura, no de
 * línea. La cuota de cada tipo se calcula sobre la base agregada de todas
 * las líneas de ese tipo (ya redondeadas a céntimos), no como la suma de
 * las cuotas de línea: ambos valores pueden diferir por el redondeo (ver
 * los tests). Las cuotas por línea que expone este módulo son puramente
 * informativas.
 *
 * Esta función no resuelve `taxId → rate`: cada llamador sigue resolviendo
 * el tipo de IVA con su propia búsqueda (manteniendo su semántica actual);
 * aquí solo se recibe el tipo ya resuelto, en decimal (0,21 = 21 %).
 */

/** Línea de entrada con el tipo de IVA ya resuelto a decimal (0.21 = 21%). */
export interface LineaImporteEntrada {
  cantidad: number;
  precioUnitario: number;
  rate: number;
}

/** Línea calculada: importes en céntimos enteros. La cuota es informativa. */
export interface LineaImporteCalculada {
  baseCentimos: number;
  cuotaCentimos: number;
  rate: number;
}

/** Grupo por tipo de IVA: base agregada y cuota autoritativa, en céntimos. */
export interface GrupoIvaCalculado {
  rate: number;
  baseCentimos: number;
  cuotaCentimos: number;
}

export interface ResultadoImportes {
  lineas: LineaImporteCalculada[];
  grupos: GrupoIvaCalculado[];
  baseTotalCentimos: number;
  cuotaTotalCentimos: number;
  irpfCentimos: number;
  totalCentimos: number;
}

/**
 * Redondea un importe en euros a céntimos enteros, mitad hacia arriba y
 * simétrico para negativos (half away from zero: 0,005 → 0,01, -0,005 →
 * -0,01; necesario para las rectificativas con importes negativos).
 *
 * `Math.round((x + Number.EPSILON) * 100)` no basta: para importes como
 * 4.780,105 el error de representación en coma flotante (4.780,10499…)
 * supera a `Number.EPSILON` en esa magnitud. En su lugar, normalizamos con
 * `toPrecision(15)`: un double tiene ~15-17 dígitos decimales significativos
 * de precisión, así que quedarnos con los primeros 15 recupera el valor
 * decimal realmente pretendido (509,355, no 509,35499999999997) y descarta
 * el ruido de los últimos bits de la mantisa. Repetimos la normalización
 * tras multiplicar por 100 porque ese producto puede introducir el mismo
 * tipo de ruido (4.780,105 × 100 = 478.010,49999999994).
 */
export function redondearACentimos(valorEnEuros: number): number {
  if (!Number.isFinite(valorEnEuros)) {
    throw new Error(`Importe no finito: ${valorEnEuros}`);
  }
  if (valorEnEuros === 0) {
    return 0;
  }

  const signo = valorEnEuros < 0 ? -1 : 1;
  const absoluto = Math.abs(valorEnEuros);

  const normalizado = Number(absoluto.toPrecision(15));
  const centimosConRuido = normalizado * 100;
  const centimosNormalizados = Number(centimosConRuido.toPrecision(15));

  // Ya sin ruido relevante: sumar 0,5 y truncar redondea "mitad hacia
  // arriba" en el dominio entero.
  const centimos = Math.floor(centimosNormalizados + 0.5);
  return signo * centimos;
}

/**
 * Convierte céntimos enteros a un número en euros con 2 decimales exactos
 * (82961 → 829.61). Equivalente a `Number(centimosATexto(centimos))`: pasa
 * por el texto para evitar que `centimos / 100` finja más precisión de la
 * que hay (p. ej. el ruido de representación de una división en coma
 * flotante), igual que hace `centimosATexto`. Nombre explícito para los
 * puntos de llamada que necesitan el número en euros, no el texto.
 */
export function centimosAEuros(centimos: number): number {
  return Number(centimosATexto(centimos));
}

/**
 * Sanea un importe que puede venir corrupto antes de pasarlo a
 * `calcularImportes` (p. ej. una factura cargada o importada desde PDF con
 * `cantidad: undefined` o `precioUnitario: "1.234,56"` como texto, que en
 * aritmética da `NaN`). `calcularImportes` lanza ante un importe no finito
 * a propósito: es el comportamiento correcto en el servidor (fail-fast; el
 * servidor valida y recalcula, así que un dato corrupto debe romper la
 * emisión, no colarse como "NaN.00" en un Facturae o un registro VeriFactu).
 *
 * Los formularios del cliente (`FacturaForm.tsx`, `RectificativaForm.tsx`),
 * en cambio, llaman a `calcularImportes` dentro de un `useMemo` que se
 * ejecuta DURANTE EL RENDER del resumen de totales: si lanza ahí, React
 * desmonta el formulario entero (sin ErrorBoundary) y el usuario pierde los
 * cambios sin guardar, donde antes solo se veía un "NaN" en el resumen. Por
 * eso esos formularios sanean cada `cantidad`/`precioUnitario` con esta
 * función SOLO para el cálculo del resumen que se muestra en pantalla; el
 * cuerpo que se envía al guardar sigue llevando los valores originales del
 * item, y es el servidor quien valida y recalcula (y quien debe seguir
 * fallando fuerte si el dato es corrupto).
 */
export function importeFinitoOCero(valor: unknown): number {
  const numero = typeof valor === "number" ? valor : Number(valor);
  return Number.isFinite(numero) ? numero : 0;
}

/** Formatea céntimos enteros como texto decimal de 2 decimales ("4780.11"). */
export function centimosATexto(centimos: number): string {
  if (!Number.isInteger(centimos)) {
    throw new Error(`Se esperaban céntimos enteros, se recibió: ${centimos}`);
  }
  const signo = centimos < 0 ? "-" : "";
  const absoluto = Math.abs(centimos);
  const parteEntera = Math.floor(absoluto / 100);
  const parteDecimal = String(absoluto % 100).padStart(2, "0");
  return `${signo}${parteEntera}.${parteDecimal}`;
}

/**
 * Calcula, en céntimos enteros, la base y la cuota de cada línea, la base y
 * cuota agregadas por cada tipo de IVA (orden de primera aparición), los
 * totales de base y cuota, el IRPF (sobre la base total, 0 si no se aplica)
 * y el total final: total = base + cuota − IRPF.
 */
export function calcularImportes(
  lineas: LineaImporteEntrada[],
  irpfRate = 0
): ResultadoImportes {
  const lineasCalculadas: LineaImporteCalculada[] = [];
  const gruposPorClave = new Map<number, GrupoIvaCalculado>();
  const ordenClaves: number[] = [];

  for (const linea of lineas) {
    const baseCentimos = redondearACentimos(linea.cantidad * linea.precioUnitario);
    const cuotaCentimos = redondearACentimos((baseCentimos / 100) * linea.rate);
    lineasCalculadas.push({baseCentimos, cuotaCentimos, rate: linea.rate});

    // Clave de agrupación en "puntos básicos" (rate en decimal × 10000: 0,21
    // → 2100). Dos líneas cuyo `rate` redondea al mismo punto básico —es
    // decir, que difieren en menos de 0,00005 en decimal, 0,005 puntos
    // porcentuales— se fusionan en el mismo grupo de IVA. El `rate` que queda
    // asociado al grupo (y con el que se calcula su cuota agregada) es el de
    // la PRIMERA línea que crea ese grupo; las líneas siguientes con un rate
    // ligeramente distinto dentro de esa tolerancia aportan su base al grupo,
    // pero su propio `cuotaCentimos` de línea (informativo, arriba) sigue
    // calculado con su rate exacto, no con el del grupo.
    const clave = Math.round(linea.rate * 10000);
    let grupo = gruposPorClave.get(clave);
    if (!grupo) {
      grupo = {rate: linea.rate, baseCentimos: 0, cuotaCentimos: 0};
      gruposPorClave.set(clave, grupo);
      ordenClaves.push(clave);
    }
    grupo.baseCentimos += baseCentimos;
  }

  const grupos = ordenClaves.map((clave) => {
    const grupo = gruposPorClave.get(clave) as GrupoIvaCalculado;
    grupo.cuotaCentimos = redondearACentimos((grupo.baseCentimos / 100) * grupo.rate);
    return grupo;
  });

  const baseTotalCentimos = grupos.reduce((acc, g) => acc + g.baseCentimos, 0);
  const cuotaTotalCentimos = grupos.reduce((acc, g) => acc + g.cuotaCentimos, 0);
  const irpfCentimos = irpfRate
    ? redondearACentimos((baseTotalCentimos / 100) * irpfRate)
    : 0;
  const totalCentimos = baseTotalCentimos + cuotaTotalCentimos - irpfCentimos;

  return {
    lineas: lineasCalculadas,
    grupos,
    baseTotalCentimos,
    cuotaTotalCentimos,
    irpfCentimos,
    totalCentimos,
  };
}
